import { InfoCircle, Star06, XClose } from '@untitled-ui/icons-react/build/cjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import useGoogleMaps from 'src/hooks/useGoogleMaps';
import { RootState } from 'src/redux/store';

import ActionTooltip from '../ActionTooltip';
import ErrorMsg from '../errorMsg';

export const initAddress: any = {
    fullAddress: '',
    address1: '',
    city: '',
    state: '',
    country: '',
    postal: '',
    latitude: '',
    longitude: '',
    stateCode: '',
    countryCode: '',
    companyName: '',
    streetNumber: '',
};

const AutoCompleteGoogle = ({
    inputName,
    placeholder,
    errorText,
    className,
    label,
    labelClassName,
    required,
    onChangeFunc,
    onAddressInputChange,
    listClassName,
    isClearable,
    mapParentClass,
    labelRight,
    labelWrapClassName,
    inputWrapClass,
    isLoading,
    isShowAiStar,
    isShowDisableClass = false,
    value,
    disabled = false,
    showError = false,
    isShowCity = false,
    isStrictCityStateOnly = false,
    errorTextTooltip = '',
    ...props
}: any) => {
    let autoComplete: any;
    const [inputValue, setInputValue] = useState('');
    const [isInputValue, setIsInputValue] = useState('');
    const [ctrlPressedCount, setCtrlPressedCount] = useState<number>(0);
    const [containerHeight, setContainerHeight] = useState<number | null>(null);
    const autoCompleteRef = useRef(null);
    const { googleKey } = useSelector((state: RootState) => state.basic);
    const { isReady } = useGoogleMaps();
    let isRecheckAddress = false;

    function extractAddressComponents(result: any, details: any) {
        result.address_components.forEach((component: any) => {
            switch (true) {
                case component.types.includes('postal_code'):
                    details.postalCode = component.long_name.length > 3 ? component.long_name : details.postalCode;
                    break;
                case component.types.includes('locality'):
                    details.city = component.long_name;
                    break;
                case component.types.includes('administrative_area_level_1'):
                    details.state = component.long_name;
                    break;
                case component.types.includes('country'):
                    details.country = component.long_name;
                    break;
                default:
                    details.address += component.types.some((type: any) => ['premise', 'street_number', 'route', 'sublocality'].includes(type)) ? `${component.long_name} ` : '';
                    break;
            }
        });
    }

    const checkNearByAddress = async (lat: number, lng: number, retryCount = 0): Promise<any> => {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleKey}`).catch((error) => {
            console.error('Failed to fetch geocode data:', error);
            throw new Error('Network error');
        });

        const details = {
            postalCode: '',
            city: '',
            state: '',
            country: '',
            address: '',
        };

        if (response.ok) {
            const data = await response.json();

            if (data.status === 'OK') {
                data.results.forEach((result: any) => {
                    extractAddressComponents(result, details);
                });
            }
        }

        if (!details.address) {
            details.address = details.city;
        }

        if (
            (!details.address || !details.city || !details.postalCode || !details.state || !details.country || details.postalCode?.length < 4) &&
            retryCount < 3 && // Limit the number of retries to 3
            !isRecheckAddress
        ) {
            isRecheckAddress = true;

            const newLat = lat + (Math.random() - 0.5) * 0.01;
            const newLng = lng + (Math.random() - 0.5) * 0.01;

            return newLat && newLng && checkNearByAddress(newLat, newLng, retryCount + 1);
        }

        return details;
    };

    const handlePlaceSelect = async () => {
        const addressObject = autoComplete.getPlace();

        if (!addressObject?.address_components) {
            return;
        }

        let latitude = addressObject?.geometry?.location.lat();
        let longitude = addressObject?.geometry?.location.lng();
        const fullAddress = addressObject.formatted_address;
        let city = '';
        let state = '';
        let postcode = '';
        let address = '';
        let country = '';
        let stateCode = '';
        let countryCode = '';
        let companyName = '';
        let isCity = false;
        let phoneNumber = null;
        let streetNumber = '';

        if (addressObject?.types?.includes('establishment')) {
            companyName = addressObject.name;
        }

        addressObject.address_components.forEach((component: any) => {
            const componentType = component.types;

            if (componentType.includes('premise')) {
                address += `${component.long_name} `;
            } else if (componentType.includes('street_number')) {
                streetNumber = component.long_name;
            } else if (componentType.includes('route')) {
                address += `${component.short_name} `;
            } else if (componentType.includes('sublocality')) {
                address += `${component.long_name} `;
            } else if (componentType.includes('postal_code')) {
                postcode = `${component.long_name}${postcode}`;
            } else if (componentType.includes('locality')) {
                city = component.long_name;
                isCity = true;
            } else if (componentType.includes('administrative_area_level_1')) {
                state = component.long_name;
                stateCode = component.short_name;
            } else if (componentType.includes('country')) {
                country = component.long_name;
                countryCode = component.short_name;
            }

            if (!isCity && (componentType.includes('sublocality_level_1') || componentType.includes('sublocality'))) {
                city = component.long_name;
            }
        });

        let extraAddress;
        let address1;

        if (address) {
            extraAddress = fullAddress.substring(0, fullAddress.indexOf(address.split(' ')[0]));
            address1 = `${extraAddress} ${address}`;
        } else {
            address1 = city;
        }

        if (!address1 || !city || !postcode || !state || !country || postcode.length < 4) {
            let addressDetails: any = await checkNearByAddress(latitude, longitude);

            if (!address1) {
                address1 = addressDetails.address;
            }

            if (!city) {
                city = addressDetails.city;
            }

            if (!postcode || postcode.length < 4) {
                postcode = addressDetails.postalCode;
            }

            if (!state) {
                state = addressDetails.state;
            }

            if (!country) {
                country = addressDetails.country;
            }
        }

        if (addressObject?.formatted_phone_number && addressObject?.formatted_phone_number !== '') {
            try {
                phoneNumber = parsePhoneNumberFromString(`+1${addressObject?.formatted_phone_number}`)?.number;
            } catch (error) {
                console.error('Error parsing phone number:', error);
            }
        }

        if (address1 && streetNumber) {
            address1 = address1.replace(streetNumber, '');
        }

        const addressObj = {
            fullAddress,
            address1: address1.trim(),
            city,
            state,
            country,
            postal: postcode,
            latitude,
            longitude,
            stateCode,
            countryCode,
            companyName,
            phoneNumber,
            streetNumber,
        };
        onAddressInputChange(addressObj.fullAddress);
        onChangeFunc(addressObj);
    };

    const handleChange = (event: any) => {
        const { value: isValue } = event.target;

        if (isValue === '') {
            onChangeFunc(initAddress);
        }
        setInputValue(isValue);
        setIsInputValue(isValue);
        onAddressInputChange(isValue);
    };

    const removeClass = () => {
        const containerElement = document.querySelectorAll('.pac-container');
        containerElement.forEach((container: any) => {
            container.classList.remove(listClassName);
        });
    };

    const handleKeyPress = (event: any) => {
        if (event.ctrlKey) {
            setCtrlPressedCount((prev) => prev + 1);
        }
    };

    useEffect(() => {
        const autoCompleteDropdown = autoCompleteRef.current as any;

        const positionDropdown = () => {
            const containerElement = document.querySelectorAll('.pac-container');

            if (containerElement) {
                containerElement.forEach((container: any) => {
                    container.classList.add(listClassName);

                    if (listClassName === 'showAddressOnTop') {
                        const inputRect = autoCompleteDropdown.getBoundingClientRect();

                        const height = container.offsetHeight;

                        if (height === 0) {
                            setContainerHeight(0);
                        } else {
                            setContainerHeight(height);
                            container.style.top = `${inputRect.top + window.scrollY - height - 20}px`;
                            container.style.left = `${inputRect.left}px`;
                        }
                    }
                });
            }
        };

        setTimeout(() => {
            positionDropdown();
            autoCompleteDropdown.addEventListener('focus', positionDropdown);
            autoCompleteDropdown.addEventListener('input', positionDropdown);

            autoCompleteDropdown.addEventListener('blur', () => {
                const containerElement = document.querySelectorAll('.pac-container');

                if (listClassName && containerElement) {
                    containerElement.forEach((container: any) => {
                        container.classList.remove(listClassName);
                    });
                }
            });
        }, 500);

        return () => {
            document.removeEventListener('mousedown', removeClass);
            autoCompleteDropdown.removeEventListener('focus', positionDropdown);
            autoCompleteDropdown.removeEventListener('input', positionDropdown);
        };
    }, [autoCompleteRef, inputValue, ctrlPressedCount, containerHeight]);

    useEffect(() => {
        const autoCompleteDropdown = autoCompleteRef.current as any;

        const positionDropdown = () => {
            const containerElement = document.querySelectorAll('.pac-container');

            if (listClassName && containerElement) {
                if (listClassName === 'showAddressOnTop') {
                    const inputRect = autoCompleteDropdown.getBoundingClientRect();

                    containerElement.forEach((container: any) => {
                        container.style.top = `${inputRect.top + window.scrollY - container.offsetHeight - 20}px`;
                        container.style.left = `${inputRect.left}px`;
                    });
                }
            }
        };

        if (window.google && window.google.maps && window.google.maps.places) {
            autoComplete = new window.google.maps.places.Autocomplete(autoCompleteRef.current, {
                types: isStrictCityStateOnly ? ['(regions)'] : isShowCity ? ['(cities)'] : [],
                componentRestrictions: { country: ['us', 'ca'] },
            });
            autoComplete.setFields(['address_components', 'formatted_address', 'formatted_phone_number', 'geometry', 'name', 'types']);
            autoComplete.addListener('place_changed', handlePlaceSelect);

            setTimeout(() => {
                positionDropdown();
            }, 500);
        }

        return () => {
            document.removeEventListener('mousedown', removeClass);
        };
        // isReady is required: the Maps script is now loaded on demand, so on first mount it is
        // usually NOT there yet. With `[]` this effect would run once, fail the window.google
        // guard above, and the autocomplete would never attach.
    }, [isReady]);

    const clearInput = () => {
        handleChange({ target: { name: inputName, value: '' } });
    };

    useEffect(() => {
        setIsInputValue(value);
    }, [value]);

    return (
        <div className={` ${mapParentClass}`}>
            <div className={`flex justify-between items-start gap-x-2 ${labelWrapClassName}`}>
                <div className="flex">
                    {label && <label className={`form_label ${labelClassName}`}>{label}</label>}
                    {required && <span className="text-red-600 ms-[2px] leading-4 text-xs">*</span>}
                </div>
                {labelRight && <>{labelRight}</>}
            </div>
            <div className={` relative flex justify-center items-center ${isShowAiStar ? 'ai-gradient-border-wrap' : ''}`}>
                {isShowAiStar && (
                    <div className="absolute top-1/2 -translate-y-1/2 left-2 w-4 h-4 mt-px">
                        <ActionTooltip message={'Detected with AI'} isOpenChildTopPosOnTop={true} tooltipPosition="center" parentClassName="min-w-[120px]" openOnHover={true}>
                            <Star06 className="w-4 h-4 text-primary " />
                        </ActionTooltip>
                    </div>
                )}
                <div className={`relative w-full flex rounded-lg justify-center items-center ${isLoading ? 'custom-loading' : ''} ${inputWrapClass}`}>
                    <input
                        type="text"
                        name={inputName}
                        ref={autoCompleteRef}
                        className={`auto-complete-google-input mfnw__login-form-control ${
                            isShowDisableClass ? 'disabled:border-borderPrimary disabled:bg-gray50 disabled:text-gray500 [&:disabled>svg]:hidden disabled-input disabled:opacity-100' : ''
                        } ${className} ${isShowAiStar ? 'pl-7' : ''}${errorText ? 'border border-error is-invalid' : ''}`}
                        placeholder={placeholder}
                        onChange={handleChange}
                        onKeyDown={handleKeyPress}
                        onKeyUp={handleKeyPress}
                        required={required}
                        value={value}
                        disabled={disabled}
                        {...props}
                    />
                    {errorTextTooltip && (
                        <div className={`absolute top-1/2 -translate-y-1/2 ${isClearable && isInputValue !== '' && !disabled ? 'right-7' : 'right-2'}`}>
                            <ActionTooltip message={errorTextTooltip} isOpenChildTopPosOnTop={true} tooltipPosition="center" parentClassName="min-w-[220px]" openOnHover={true} type="light">
                                <InfoCircle id="info-circle" className="text-red-500 h-4 w-4 cursor-pointer clear" />
                            </ActionTooltip>
                        </div>
                    )}
                    {isClearable && isInputValue !== '' && !disabled && <XClose id="clear" className="h-4 w-4 cursor-pointer absolute right-2 clear" onClick={clearInput} />}
                </div>
            </div>
            {showError && errorText && typeof errorText !== 'boolean' && <ErrorMsg errorText={errorText} />}
        </div>
    );
};

export default AutoCompleteGoogle;
